import { GetStaticProps } from 'next';
import { PrismicDocument } from '@prismicio/client';
import { growthBookService, prismicService } from '@services/businessContainer';
import {
  IPrismicCustomTypeContent,
  SlicesContainer,
  getPageContentsWithProducts,
} from '@screens/SlicesContainer';
import BreadcrumbList from '@components/BreadcrumbList';
import { OrganizationJsonLd, NextSeo } from 'next-seo';
import getConfig from 'next/config';
import CustomError from '@screens/CustomError';
import { useEffect } from 'react';
import { fireAutoMLEvent } from '@utils/tracking';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { formatPageContents } from '@utils/prismic';
import { useTranslations } from 'next-intl';
import { getChannelFromLocale } from '@monofront/business/utils';
import withUnauthentication from '@hocs/withUnauthentication';
import { useCookies } from 'react-cookie';
import {
  HOME_SLICES_LINKS,
  PRODUCT_BUNDLES_LINKS,
  WIDGETS_LINKS,
} from '@constants/prismic';
import { LFPage, injectLayoutData } from '@utils/layout';
import useLocalizedOrganizationInfos from '@hooks/i18n/useLocalizedOrganizationInfos';

const {
  publicRuntimeConfig: { appBaseUrl, domains },
} = getConfig();

interface IHomeProps {
  pageContents?: IPrismicCustomTypeContent[];
  abTestContents?: IPrismicCustomTypeContent[];
  abTestFeatureFlagKey?: string;
}

const Home: LFPage<IHomeProps> = ({
  pageContents,
  abTestContents,
  abTestFeatureFlagKey,
}) => {
  const [cookies] = useCookies(['lfAuth']);
  const isAbTestEnabled =
    abTestFeatureFlagKey &&
    abTestContents &&
    abTestContents.length > 0 &&
    growthBookService.growthbook.isOn(abTestFeatureFlagKey);

  useEffect(() => fireAutoMLEvent('home-page-view'), []);

  const tGlobal = useTranslations('Global');
  const tSEODEFAULT = useTranslations('SEO.DEFAULT');
  const {
    address,
    contactPoint,
    description,
    email,
    hasMerchantReturnPolicy,
    logo,
    name,
    sameAs,
    url,
    vatID,
  } = useLocalizedOrganizationInfos();

  if (!pageContents || pageContents.length === 0) {
    return <CustomError statusCode={404} />;
  }

  const languageAlternates = domains.map((domain) => ({
    hrefLang: domain.locale,
    href: `https://${domain.domain}/`,
  }));

  if (cookies.lfAuth === 'true') {
    return (
      <>
        <NextSeo
          canonical={appBaseUrl}
          openGraph={{
            url: appBaseUrl,
          }}
          languageAlternates={languageAlternates}
        />
        <div className="loader-container">
          <HeartLoader />
        </div>
        <style jsx>
          {`
            .loader-container {
              min-height: calc(100vh - 100px);
              display: flex;
              align-items: center;
              padding-bottom: 100px;
            }
          `}
        </style>
      </>
    );
  }

  return (
    <>
      <BreadcrumbList
        name={`${tGlobal('companyName')} | ${tGlobal('defaultBreadcrumb')}`}
      />
      <NextSeo
        description={tSEODEFAULT('description')}
        canonical={`${appBaseUrl}/`}
        openGraph={{
          url: appBaseUrl,
        }}
        languageAlternates={languageAlternates}
      />
      <OrganizationJsonLd
        type="OnlineStore"
        name={name}
        url={url}
        description={description}
        sameAs={sameAs}
        logo={logo}
        email={email}
        address={address}
        contactPoint={contactPoint}
        vatID={vatID}
        hasMerchantReturnPolicy={hasMerchantReturnPolicy}
      />
      <SlicesContainer
        pageContents={isAbTestEnabled ? abTestContents! : pageContents}
      />
      <style jsx>
        {`
          .loader-container {
            min-height: calc(100vh - 100px);
            display: flex;
            align-items: center;
            padding-bottom: 100px;
          }
        `}
      </style>
    </>
  );
};

export const getStaticProps = injectLayoutData<
  IHomeProps,
  GetStaticProps<IHomeProps>
>(async ({ locale, previewData }) => {
  const document: PrismicDocument = await prismicService.getDocumentByUid(
    'homepage',
    'not-connected-homepage',
    previewData,
    {
      fetchLinks: [
        ...HOME_SLICES_LINKS,
        ...WIDGETS_LINKS,
        ...PRODUCT_BUNDLES_LINKS,
      ],
      ...(locale ? { lang: `${locale}-${locale}` } : {}),
    },
  );

  if (!document) {
    return {
      notFound: true,
    };
  }

  const pageContentsWithProducts = await getPageContentsWithProducts(
    formatPageContents(document.data.group),
    getChannelFromLocale(locale),
  );

  const abTestContentsWithProducts: IPrismicCustomTypeContent[] = [];

  const abTestFeatureFlagKey = document.data.feature_flag_key;

  abTestContentsWithProducts.push(
    ...(await getPageContentsWithProducts(
      formatPageContents(document.data.ab_test_group),
      getChannelFromLocale(locale),
    )),
  );

  return {
    props: {
      pageContents: pageContentsWithProducts,
      abTestContents: abTestContentsWithProducts,
      isExperimentActive: document.data.ab_test_enabled || false,
      abTestFeatureFlagKey,
    },
    revalidate: 1800, // 30min
  };
});

export default withUnauthentication(Home);
